import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Grid,
  Box,
  Typography
} from '@mui/material';
import apiClient from "../../request/http-common";
import { useMutation, useQuery } from "react-query";

const CreateCustomPlan = ({ open, handleClose, userId, onSuccess }) => {
  const [formData, setFormData] = useState({
    user_id: userId,
    investment_plan_id: '',
    name: '',
    amount_invested: '',
    min_amount: '',
    bonus_per_downline: '',
    number_of_downlines: '',
    total_earning: '',
    initial_earning: '',
    duration: '',
    amount_per_hour: '',
    extra_note: ''
  });

  const { data: investmentPlans } = useQuery('investment-plans', async () => {
    const response = await apiClient.get('/api/investment-plans');
    return response.data.plans;
  });

  const { mutate: createCustomPlan, isLoading } = useMutation(
    async () => {
      return await apiClient.post('/api/custom-plans', formData);
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          localStorage.setItem('notification', JSON.stringify({
            message: res.data.message,
            type: 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          handleClose();
          if (onSuccess) onSuccess();
          resetForm();
        }
      },
      onError: (err) => {
        const errorMessage = err.response?.data.message || 'An error occurred';
        localStorage.setItem('notification', JSON.stringify({
          message: errorMessage,
          type: 'error'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
      }
    }
  );

  const handleBasePlanSelect = (planId) => {
    if (!planId) {
      // Reset form if no plan selected
      resetForm();
      return;
    }

    // Find the selected plan from the loaded plans
    const selectedPlan = investmentPlans?.find(plan => plan.id === planId);
    
    if (selectedPlan) {
      // Pre-fill the form with the selected plan's data
      setFormData({
        user_id: userId,
        investment_plan_id: selectedPlan.id,
        name: selectedPlan.name,
        amount_invested: selectedPlan.amount_invested,
        min_amount: selectedPlan.min_amount,
        bonus_per_downline: selectedPlan.bonus_per_downline,
        number_of_downlines: selectedPlan.number_of_downlines,
        total_earning: selectedPlan.total_earning,
        initial_earning: selectedPlan.initial_earning,
        duration: selectedPlan.duration,
        amount_per_hour: selectedPlan.amount_per_hour || '',
        extra_note: selectedPlan.extra_note || ''
      });
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: userId,
      investment_plan_id: '',
      name: '',
      amount_invested: '',
      min_amount: '',
      bonus_per_downline: '',
      number_of_downlines: '',
      total_earning: '',
      initial_earning: '',
      duration: '',
      amount_per_hour: '',
      extra_note: ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    if (userId) {
      setFormData(prev => ({ ...prev, user_id: userId }));
    }
  }, [userId]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Custom Investment Plan</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can either select a base plan to customize or create from scratch
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Base Plan (Optional)</InputLabel>
            <Select
              value={formData.investment_plan_id}
              label="Select Base Plan (Optional)"
              onChange={(e) => handleBasePlanSelect(e.target.value)}
            >
              <MenuItem value="">
                <em>Create from scratch</em>
              </MenuItem>
              {investmentPlans?.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maximum Amount"
                name="amount_invested"
                type="number"
                value={formData.amount_invested}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Amount"
                name="min_amount"
                type="number"
                value={formData.min_amount}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bonus Per Downline (%)"
                name="bonus_per_downline"
                type="number"
                value={formData.bonus_per_downline}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Downlines"
                name="number_of_downlines"
                type="number"
                value={formData.number_of_downlines}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Earning (%)"
                name="total_earning"
                type="number"
                value={formData.total_earning}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Initial Earning (%)"
                name="initial_earning"
                type="number"
                value={formData.initial_earning}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Duration (comma separated: e.g., 30 Days,60 Days,90 Days)"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                helperText="Enter durations separated by commas"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount Per Hour"
                name="amount_per_hour"
                type="number"
                value={formData.amount_per_hour}
                onChange={handleChange}
                required
                helperText="Enter the hourly earning rate"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Extra Note (Optional)"
                name="extra_note"
                value={formData.extra_note}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="This note will appear below the Invest Now button"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={createCustomPlan} 
          variant="contained" 
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Custom Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCustomPlan;

